/*
 * The three kinds of service, as data: what each one asks the admin for, and
 * what the fields it never asks about are set to. Everything the add/edit flow
 * varies by category reads off this table.
 */

import { CATEGORIES } from "../../../domain/influencerService";

const ALL_FIELDS = Object.freeze([
  "price",
  "label",
  "description",
  "maxWordCount",
  "requiresTextInput",
  "maxQuantity",
  "exclusionGroup",
]);

const HIDDEN_FIELD_VALUES = Object.freeze({
  maxWordCount: null,
  requiresTextInput: true,
  maxQuantity: 1,
  exclusionGroup: null,
});

export const SERVICE_TYPES = Object.freeze({
  [CATEGORIES.QUOTE]: Object.freeze({
    noun: "Quote",
    blurb: "A short line the fan asks you to write.",
    isAdvanced: false,
    fields: Object.freeze(["price", "label", "description", "maxWordCount"]),
    placeholders: Object.freeze({
      label: "Short Quote (up to 5 words)",
      description: "A short personalized quote inscription",
      maxWordCount: "5",
    }),
  }),
  [CATEGORIES.DOODLE]: Object.freeze({
    noun: "Doodle",
    blurb: "A small drawing the fan asks you for.",
    isAdvanced: false,
    fields: Object.freeze(["price", "label", "description"]),
    placeholders: Object.freeze({
      label: "Custom Doodle",
      description: "A quick hand-drawn sketch",
    }),
  }),
  [CATEGORIES.CUSTOM]: Object.freeze({
    noun: "Custom Service",
    blurb: "Anything else. You choose every option.",
    isAdvanced: true,
    fields: ALL_FIELDS,
    placeholders: Object.freeze({
      label: "Character name stamp",
      description: "What the shopper gets",
      maxWordCount: "5",
    }),
  }),
});

export const SERVICE_TYPE_ORDER = Object.freeze([
  CATEGORIES.QUOTE,
  CATEGORIES.DOODLE,
  CATEGORIES.CUSTOM,
]);

export const nounFor = (category) => SERVICE_TYPES[category]?.noun ?? "Service";

export const placeholdersFor = (category) =>
  SERVICE_TYPES[category]?.placeholders ?? {};

/*
 * A category the table does not know falls back to every field, so a service
 * created before this table existed stays editable rather than losing values
 * to a form that cannot show them.
 */
export const fieldsFor = (category, { advancedRevealed = false } = {}) =>
  advancedRevealed ? ALL_FIELDS : SERVICE_TYPES[category]?.fields ?? ALL_FIELDS;

export const isFieldShown = (category, field, options) =>
  fieldsFor(category, options).includes(field);

// The fields this type never asks about — what the advanced disclosure holds.
export const hiddenFieldsFor = (category) =>
  ALL_FIELDS.filter((field) => !isFieldShown(category, field));

export const defaultsFor = (category) =>
  Object.fromEntries(
    hiddenFieldsFor(category)
      .filter((field) => Object.hasOwn(HIDDEN_FIELD_VALUES, field))
      .map((field) => [field, HIDDEN_FIELD_VALUES[field]]),
  );
